import { ReviewerArtifactLinks } from './ReviewerReviewContext'
import ScreenshotViewer from './ScreenshotViewer'
import { Suspense, useState } from 'react'
import { Clock3, ExternalLink, UserRound } from 'lucide-react'
import { type ReviewerKey, type ReviewFilter, type ReviewerResponse } from '../schemas/sessionReviewers'
import { useGetSessionReviewerInfiniteQuery } from '../services/sessionReviewersApi'
import { Link } from './nav'
import { lazyWithRetry } from '../lib/lazyWithRetry'
import ChunkErrorBoundary from './ChunkErrorBoundary'
import ReviewerBugList from './bug-management/ReviewerBugList'
import './SessionReviewers.css'

// recharts consumer — loaded as a deferred chunk so the library stays out of the eagerly-executed
// entry bundle (QA bug bug-mv5s0n2s-i8jh, issue #5955).
const ReviewerObservationChart = lazyWithRetry(() => import('./ReviewerObservationChart'))

type Run = ReviewerResponse['runs'][number]

function ObservationCard({ observation, deletedCodes, filter }: {
  observation: { code: string; description: string; attributes: Record<string, unknown> }
  deletedCodes: string[]
  filter: ReviewFilter
}) {
  if (deletedCodes.includes(observation.code)) return null
  if (filter.code && observation.code !== filter.code) return null
  if (!Object.entries(filter.attributes).every(([key, value]) => observation.attributes[key] === value)) return null

  const attrs = Object.entries(observation.attributes)
  return <details className="session-reviewers__observation session-reviewers__observation--collapsible">
    <summary className="session-reviewers__observation-toggle">
      <span className="session-reviewers__observation-code">{observation.code}</span>
      {attrs.length > 0 && <span className="session-reviewers__observation-attr-count">{attrs.length} {attrs.length === 1 ? 'attr' : 'attrs'}</span>}
    </summary>
    <div className="session-reviewers__observation-body">
      <p>{observation.description}</p>
      {attrs.length > 0 && <dl>{attrs.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}</dl>}
    </div>
  </details>
}

function SessionCard({ run, data, projectId, reviewer, filter }: {
  run: Run
  data: ReviewerResponse
  projectId: string
  reviewer: ReviewerKey
  filter: ReviewFilter
}) {
  const visibleObservations = run.output?.observations.filter(o =>
    !data.deleted_codes.includes(o.code)
    && (!filter.code || o.code === filter.code)
    && Object.entries(filter.attributes).every(([key, value]) => o.attributes[key] === value)
  ) ?? []
  const allDeleted = run.output?.observations.every(o => data.deleted_codes.includes(o.code))

  return <article className="session-reviewers__run">
    <header className="session-reviewers__session-header">
      <div className="session-reviewers__session-identity">
        <span className="session-reviewers__session-avatar" aria-hidden="true"><UserRound size={20} /></span>
        <div className="session-reviewers__session-info">
          <h4>{run.user_email || 'Anonymous user'}</h4>
          <div className="session-reviewers__session-time"><Clock3 size={14} aria-hidden="true" />
            {run.session_started_at
              ? <time dateTime={run.session_started_at}>{new Date(run.session_started_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}</time>
              : <span>Session time unavailable</span>}
          </div>
        </div>
      </div>
      <div className="session-reviewers__session-actions">
        <span className={`session-reviewers__status-badge session-reviewers__status-badge--${run.status}`}>{run.status}</span>
        <a className="session-reviewers__fullstory-link" href={run.session_url} target="_blank" rel="noreferrer">View in FullStory <ExternalLink size={14} aria-hidden="true" /></a>
      </div>
    </header>

    {run.latest_task_id !== run.task_id && <p className="session-reviewers__stale-notice">Latest attempt: {run.latest_status}. Showing the previous saved result.</p>}

    {/* Observation summary + expandable cards */}
    {visibleObservations.length > 0 && <div className="session-reviewers__observations-section">
      <div className="session-reviewers__observations-summary">
        <strong>{visibleObservations.length}</strong> {visibleObservations.length === 1 ? 'observation' : 'observations'}
        {visibleObservations.length > 0 && <span className="session-reviewers__observation-codes-preview">
          {visibleObservations.slice(0, 4).map(o => o.code).join(', ')}{visibleObservations.length > 4 && ` +${visibleObservations.length - 4} more`}
        </span>}
      </div>
      {visibleObservations.map((o, index) => <ObservationCard key={index} observation={o} deletedCodes={data.deleted_codes} filter={filter} />)}
    </div>}

    {allDeleted && <p className="session-reviewers__muted">No observations.</p>}

    {run.output?.screenshot && <figure className="session-reviewers__screenshot">
      <ScreenshotViewer frame={run.output.screenshot.image.url} alt={run.output.screenshot.caption} />
      <figcaption>{run.output.screenshot.caption} · {(run.output.screenshot.timestamp_ms / 1000).toFixed(3)}s into the session</figcaption>
    </figure>}

    {run.journey_id && typeof run.journey_reviewer === 'string' && run.journey_reviewer !== reviewer && (
      <p className="session-reviewers__muted">Journey attributed to <Link to={`/projects/${projectId}/session-reviewers/${run.journey_reviewer}`}>{data.reviewers.find(r => r.key === run.journey_reviewer)?.name ?? run.journey_reviewer}</Link>.</p>
    )}
    {run.context && <ReviewerArtifactLinks projectId={projectId} review={run.context as import('../schemas/sessionReviewers').ReviewerContext} />}
    {!run.journey_id && run.journey_task_id && <p className="session-reviewers__muted">Journey creation: {run.journey_status}{run.journey_status === 'empty' && ' — no replayable activity'}</p>}
  </article>
}

export default function SessionReviewerResults({ projectId, reviewer }: { projectId: string; reviewer: ReviewerKey }) {
  const [filter, setFilter] = useState<ReviewFilter>({ code: '', attributes: {} })
  const query = useGetSessionReviewerInfiniteQuery({ projectId, reviewer, filter }, { pollingInterval: 10000, skipPollingIfUnfocused: true })
  const data = query.currentData?.pages[0] ?? query.data?.pages[0]
  const runs = [...new Map((query.currentData?.pages.flatMap(page => page.runs) ?? []).map(run => [run.id, run])).values()]
  const journeys = [...new Map((query.currentData?.pages.flatMap(page => page.journeys) ?? []).map(journey => [journey.journey_id, journey])).values()]
  const config = data?.reviewers.find(r => r.key === reviewer)

  const totals = data?.totals

  return <section className="session-reviewers" aria-label="Session reviewers">
    <header className="session-reviewers__heading">
      <div>
        <h1>{config?.name ?? 'Session reviewer'}</h1>
        <p>{config?.goal}</p>
      </div>
    </header>

    {/* Quick stats bar */}
    {totals && <div className="session-reviewers__stats-bar">
      <div className="session-reviewers__stat-card">
        <span className="session-reviewers__stat-card-value">{totals.reviewed_sessions.toLocaleString()}</span>
        <span className="session-reviewers__stat-card-label">sessions reviewed</span>
      </div>
      <div className="session-reviewers__stat-card">
        <span className="session-reviewers__stat-card-value">{totals.observations.toLocaleString()}</span>
        <span className="session-reviewers__stat-card-label">observations</span>
      </div>
      {config && config.open_bugs > 0 && <div className="session-reviewers__stat-card">
        <span className="session-reviewers__stat-card-value" style={{ color: 'var(--color-danger)' }}>{config.open_bugs.toLocaleString()}</span>
        <span className="session-reviewers__stat-card-label">open bugs</span>
      </div>}
      {journeys.length > 0 && <div className="session-reviewers__stat-card">
        <span className="session-reviewers__stat-card-value">{journeys.length.toLocaleString()}</span>
        <span className="session-reviewers__stat-card-label">{journeys.length === 1 ? 'journey' : 'journeys'}</span>
      </div>}
      {totals.total_sessions > 0 && <div className="session-reviewers__stat-card">
        <span className="session-reviewers__stat-card-value">{`${totals.reviewed_sessions} / ${totals.total_sessions}`}</span>
        <span className="session-reviewers__stat-card-label">coverage</span>
      </div>}
    </div>}

    {/* Bugs — collapsed with summary count */}
    <ReviewerBugList key={reviewer} projectId={projectId} reviewer={reviewer} />

    {query.isLoading && <p>Loading reviewer…</p>}
    {query.isError && <p role="alert">Could not load session insights. <button onClick={() => query.refetch()}>Retry</button></p>}

    {data && <>
      {/* Associated journeys — collapsed */}
      {journeys.length > 0 && <details className="session-reviewers__journeys-section">
        <summary className="session-reviewers__section-toggle">
          <h3>Associated journeys</h3>
          <span className="session-reviewers__section-count">{journeys.length}</span>
        </summary>
        <ul className="session-reviewers__journeys-list">{journeys.map(journey => <li key={journey.journey_id}>
          <Link to={`/projects/${projectId}/journeys/${journey.journey_id}`}>{journey.name}</Link>
          {journey.source_reviewer !== reviewer && <span className="session-reviewers__muted"> · Originally requested by {data.reviewers.find(r => r.key === journey.source_reviewer)?.name ?? journey.source_reviewer}</span>}
        </li>)}</ul>
      </details>}

      {/* Observation chart */}
      <ChunkErrorBoundary eventTag="reviewer-observation-chart" fallback={<p role="status" className="session-reviewers__muted">Loading chart…</p>}>
        <Suspense fallback={<p role="status" className="session-reviewers__muted">Loading chart…</p>}>
          <ReviewerObservationChart series={data.series} code={filter.code} onSelect={code => setFilter(current => ({ ...current, code, attributes: {} }))} period={filter.period} onPeriodSelect={period => setFilter(current => ({ ...current, period }))} />
        </Suspense>
      </ChunkErrorBoundary>

      {/* Sessions */}
      <h3 className="session-reviewers__sessions-heading">Sessions</h3>
      {!query.currentData && query.isFetching && <p role="status">Loading sessions…</p>}
      {!runs.length && !query.isFetching && <p className="session-reviewers__muted">{filter.period || filter.code ? 'No sessions match the selected observations and time period.' : 'No sessions to display. Automatic reviews are configured through the API.'}</p>}
      {runs.map(run => <SessionCard key={run.id} run={run} data={data} projectId={projectId} reviewer={reviewer} filter={filter} />)}
      <div className="session-reviewers__load-more">
        {query.hasNextPage ? <button disabled={query.isFetching} onClick={() => void query.fetchNextPage()}>{query.isFetchingNextPage ? 'Loading…' : 'Load more'}</button> : runs.length > 0 && <span>You're all caught up</span>}
      </div>
    </>}
  </section>
}
